import { Component } from 'react';
import { SafeAreaView, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { AnalyticsEvent } from '../../constants/AnalyticsEvent';
import FreeDeliveryBar from '../../elements/FreeDeliveryBar';
import LoadingView from '../../elements/LoadingView';
import { logAnalyticsEvent } from '../../redux/actions/analyticsLog';
import { getFreeDeliveryActions } from '../../redux/actions/getFreeDeliveryActions';
import { getMemberProfileActions } from '../../redux/actions/getMemberProfileActions';
import { DeliveryMode, RoleTypes } from '../../types/Common';
import { getIsTW } from '../../utils/countryInfo';
import openBlockedUserDialog from '../../utils/dialogs/blockedUser';
import * as navigationService from '../../utils/navigationService';
import CheckoutBar from '../CheckoutBar';
import CartContent from './CartContent';
import DeliveryModal from './DeliveryModal';
import Header from './Header';
import OutOfStock from './OutOfStock';
import styles from './styles';

interface AnalyticsData {
  content_id: string[];
  price: number;
  quantity: number;
}
interface Props {
  cartIds: string[];
  outOfStockCartIds: string[];
  getMemberProfile: typeof getMemberProfileActions.request;
  getFreeDelivery: typeof getFreeDeliveryActions.request;
  getDefaultShippingFees: () => void;
  hasCartContent: boolean;
  isFocused: boolean;
  isLogin: boolean;
  isCartContentLoading: boolean;
  isSubtotalLoading: boolean;
  isBlocked: boolean;
  loadCartContent: Function;
  login: Function;
  resetTransaction: Function;
  role: RoleTypes;
  selectedAllCart: Function;
  logEvent: typeof logAnalyticsEvent;
  cartListAnalyticsData: AnalyticsData[];
  hasOutOfStock: boolean;
  hasDeliveryError: boolean;
  currentMethod: DeliveryMode;
}
interface State {
  preserveSelection: boolean;
  showDialog: boolean;
  isModalOpen: boolean;
}

const sectionsDefined = {
  cartContent: 'cartContent',
  outOfStock: 'outOfStock',
};

type SectionType = keyof typeof sectionsDefined;

const sections = Object.keys(sectionsDefined) as SectionType[];

export default class CartPage extends Component<Props, State> {
  state = {
    dialogImageUrl: undefined,
    preserveSelection: false,
    showDialog: false,
    isModalOpen: false,
  };

  constructor(props) {
    super(props);

    props.resetTransaction();
    this.fetchCartContent();
  }

  componentDidMount() {
    const {
      isLogin,
      getMemberProfile,
      role,
      getFreeDelivery,
      getDefaultShippingFees,
      cartListAnalyticsData,
      logEvent,
    } = this.props;

    getDefaultShippingFees();

    if (role === 'ghost') {
      getFreeDelivery();
    }

    if (isLogin) {
      getMemberProfile();
    }

    if (cartListAnalyticsData?.length > 0) {
      logEvent({
        logEvent: AnalyticsEvent.ViewCart,
        data: cartListAnalyticsData,
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetTransaction, isFocused, cartListAnalyticsData } = this.props;
    const { preserveSelection } = this.state;

    if (
      cartListAnalyticsData?.length > 0 &&
      cartListAnalyticsData?.length !== prevProps.cartListAnalyticsData?.length
    ) {
      this.props.logEvent({
        logEvent: AnalyticsEvent.ViewCart,
        data: cartListAnalyticsData,
      });
    }
    if (isFocused && !prevProps.isFocused) {
      resetTransaction();
      this.fetchCartContent({ preserveSelection });
    }
  }

  fetchCartContent = (args = {}) => {
    const { isFocused, role, loadCartContent } = this.props;

    if (isFocused && role !== 'ghost') {
      loadCartContent({
        ...args,
      });
    }
  };

  setPreserveSelection = preserveSelection => {
    this.setState({ preserveSelection });
  };

  onCheckoutButtonPress = () => {
    const { isBlocked, role, login } = this.props;

    if (role !== 'member') {
      login();
    } else if (isBlocked) {
      openBlockedUserDialog();
    } else {
      navigationService.navigate('CheckoutPage');
    }
  };

  renderCartContent = (key?: string) => {
    const { isFocused } = this.props;

    return <CartContent key={key} isFocused={isFocused} layoutName="cart" />;
  };

  renderOutOfStock = (key?: string) => {
    const { isFocused } = this.props;

    return (
      <OutOfStock key={key} isFocused={isFocused} layoutName="outofstock" />
    );
  };

  renderSection = ({ item }: { item: SectionType }) => {
    switch (item) {
      case 'cartContent':
        return this.renderCartContent(item);
      case 'outOfStock':
        return this.renderOutOfStock(item);
      default:
        return null;
    }
  };

  renderContent = ({ hasOutOfStock }) => {
    const { isCartContentLoading, cartIds, outOfStockCartIds } = this.props;

    const shouldShowLoadingView =
      isCartContentLoading &&
      outOfStockCartIds.length === 0 &&
      cartIds.length === 0;

    if (shouldShowLoadingView) {
      return <LoadingView />;
    }

    if (hasOutOfStock) {
      return (
        <FlatList
          key="cart-wrapper"
          style={styles.containerWrapper}
          data={sections}
          renderItem={this.renderSection}
        />
      );
    }

    return this.renderCartContent();
  };

  render() {
    const {
      isCartContentLoading,
      hasOutOfStock,
      isSubtotalLoading,
      hasCartContent,
      logEvent,
      hasDeliveryError,
      currentMethod,
    } = this.props;

    return (
      <SafeAreaView style={styles.container}>
        <Header logEvent={logEvent} />
        {getIsTW() && <FreeDeliveryBar />}
        {this.renderContent({ hasOutOfStock })}
        {hasCartContent && (
          <View style={styles.bottomView}>
            <CheckoutBar
              // @ts-ignore for test
              testID="checkoutBar"
              page="cart"
              isLoading={isCartContentLoading || isSubtotalLoading}
              onPress={this.onCheckoutButtonPress}
              setIsModalOpen={(v: boolean) => this.setState({ isModalOpen: v })}
              disableBtn={
                hasDeliveryError &&
                currentMethod === DeliveryMode.CONVENIENCE_STORE_PICKUP
              }
            />
          </View>
        )}
        <DeliveryModal
          isOpen={this.state.isModalOpen}
          setIsOpen={v => this.setState({ isModalOpen: v })}
        />
      </SafeAreaView>
    );
  }
}
